// Popularity Battle point chart — static reference for the in-game
// 1v1 battle scoring system (BGMI / PUBG Mobile):
//
//   - Each player earns BASE points from their popularity tier
//     (table below, community-verified against the in-game system).
//   - Winner  = own base points + half of the loser's base points.
//   - Loser   = half of their own base points.
//
// Pure presentation, no Firestore — values only change if the game
// updates its scoring, in which case edit POP_POINT_TABLE here.
import { Link } from "react-router-dom";

const ACCENTS = {
  BGMI: "#F88200",
  PUBG: "#258BC8",
};

// Popularity range → base points. `from` is inclusive; `to` null = open-ended.
// Exported: the battle calculator derives all its math from this table.
export const POP_POINT_TABLE = [
  { from: 0,         to: 2_499,     points: 6 },
  { from: 2_500,     to: 4_999,     points: 10 },
  { from: 5_000,     to: 7_999,     points: 14 },
  { from: 8_000,     to: 14_999,    points: 16 },
  { from: 15_000,    to: 49_999,    points: 20 },
  { from: 50_000,    to: 119_999,   points: 24 },
  { from: 120_000,   to: 259_999,   points: 28 },
  { from: 260_000,   to: 499_999,   points: 32 },
  { from: 500_000,   to: 899_999,   points: 36 },
  { from: 900_000,   to: 1_999_999, points: 40 },
  { from: 2_000_000, to: 3_999_999, points: 42 },
  { from: 4_000_000, to: null,      points: 44 },
];

const MAX_POINTS = POP_POINT_TABLE[POP_POINT_TABLE.length - 1].points;

// 2500 → "2.5K", 900000 → "900K", 2000000 → "2M". Upper bounds like
// 2,499 display as the clean tier edge ("2.5K") — tiers are
// top-exclusive, so the −1 only exists to keep ranges non-overlapping.
function fmtPop(n) {
  const clean = n % 10 === 9 ? n + 1 : n;
  if (clean >= 1_000_000) {
    const m = clean / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (clean >= 1_000) {
    const k = clean / 1_000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return String(clean);
}

function rangeLabel(row) {
  if (row.to == null) return `${fmtPop(row.from)}+`;
  return `${fmtPop(row.from)} – ${fmtPop(row.to)}`;
}

export default function PopBattlePointChart({ game }) {
  const accent = ACCENTS[game] || "#3498db";

  return (
    <section style={{ margin: "28px 0" }}>
      {/* Section header — matches the REWARDS section's pill + tag. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <span
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#0d1017",
            fontSize: 11.5, fontWeight: 900, letterSpacing: 1.8,
            boxShadow: `0 4px 18px ${accent}66`,
          }}
        >
          ⚔️ POPULARITY BATTLE POINTS TABLE
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "#a4a7b0" }}>
          {game} · 1v1 POPULARITY BATTLE
        </span>
        <Link
          to="/calculator"
          style={{
            marginLeft: "auto",
            padding: "6px 12px", borderRadius: 999,
            border: `1px solid ${accent}66`,
            background: `${accent}14`,
            color: accent, textDecoration: "none",
            fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          🧮 POINT CALCULATOR →
        </Link>
      </div>

      <div
        style={{
          borderRadius: 18,
          border: `1px solid ${accent}44`,
          background: `linear-gradient(160deg, ${accent}14 0%, rgba(255,255,255,0.02) 40%, rgba(0,0,0,0) 100%), #0e1218`,
          boxShadow: `0 20px 50px rgba(0,0,0,0.55), 0 0 40px ${accent}22`,
          overflow: "hidden",
        }}
      >
        {/* ── How points settle: WIN / LOSE formula tiles ── */}
        <div
          className="pb-pc-formulas"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            padding: "16px 16px 4px",
          }}
        >
          <div
            style={{
              borderRadius: 12,
              padding: "12px 14px",
              background: "rgba(43,181,98,0.10)",
              border: "1px solid rgba(43,181,98,0.35)",
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.8, color: "#4de37c", marginBottom: 5 }}>
              ● IF YOU WIN
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#eaeaef", letterSpacing: 0.2 }}>
              Your points{" "}
              <span style={{ color: "#4de37c" }}>+</span>{" "}
              <span style={{ color: "#4de37c" }}>½</span> opponent's points
            </div>
          </div>
          <div
            style={{
              borderRadius: 12,
              padding: "12px 14px",
              background: "rgba(233,63,76,0.08)",
              border: "1px solid rgba(233,63,76,0.32)",
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.8, color: "#ff7b85", marginBottom: 5 }}>
              ○ IF YOU LOSE
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#eaeaef", letterSpacing: 0.2 }}>
              <span style={{ color: "#ff7b85" }}>½</span> of your own points
            </div>
          </div>
        </div>

        {/* Worked examples — stated in POPULARITY (what users actually
            know about themselves), mapped through the tier table to
            points. One thousands-range example, one millions-range. */}
        <div
          style={{
            margin: "12px 16px 0",
            padding: "9px 14px",
            borderRadius: 10,
            background: "rgba(255,255,255,0.035)",
            border: "1px solid rgba(255,255,255,0.07)",
            fontSize: 12, color: "#a4a7b0", fontWeight: 600, letterSpacing: 0.2,
            display: "grid", gap: 6,
          }}
        >
          <div>
            <span style={{ color: accent, fontWeight: 900, letterSpacing: 1.2, marginRight: 8 }}>e.g.</span>
            <b style={{ color: "#eaeaef" }}>12K pop</b> (16 pts) beats{" "}
            <b style={{ color: "#eaeaef" }}>4K pop</b> (10 pts) → winner{" "}
            <b style={{ color: "#4de37c" }}>16 + 5 = 21 pts</b>, loser{" "}
            <b style={{ color: "#ff7b85" }}>5 pts</b>
          </div>
          <div>
            <span style={{ color: accent, fontWeight: 900, letterSpacing: 1.2, marginRight: 8 }}>e.g.</span>
            <b style={{ color: "#eaeaef" }}>5M pop</b> (44 pts) beats{" "}
            <b style={{ color: "#eaeaef" }}>3M pop</b> (42 pts) → winner{" "}
            <b style={{ color: "#4de37c" }}>44 + 21 = 65 pts</b>, loser{" "}
            <b style={{ color: "#ff7b85" }}>21 pts</b>
          </div>
        </div>

        {/* ── Popularity → points table ── */}
        <div style={{ padding: "14px 16px 16px" }}>
          <div
            className="pb-pc-row"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(96px, 150px) 52px 1fr",
              gap: 12,
              alignItems: "center",
              padding: "0 10px 8px",
            }}
          >
            <span style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.8, color: "#a4a7b0" }}>
              POPULARITY
            </span>
            <span style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 1.8, color: "#a4a7b0", textAlign: "right" }}>
              POINTS
            </span>
            <span aria-hidden="true" />
          </div>

          {POP_POINT_TABLE.map((row, i) => {
            const pct = (row.points / MAX_POINTS) * 100;
            const top = i === POP_POINT_TABLE.length - 1; // highest tier gets the full glow
            return (
              <div
                key={row.from}
                className="pb-pc-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(96px, 150px) 52px 1fr",
                  gap: 12,
                  alignItems: "center",
                  padding: "7px 10px",
                  borderRadius: 10,
                  background: i % 2 ? "rgba(255,255,255,0.025)" : "transparent",
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                    fontSize: 12.5, fontWeight: 700,
                    color: top ? "#fff" : "#c8ced5",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rangeLabel(row)}
                </span>
                <span
                  style={{
                    fontFamily: "'Rajdhani', system-ui, -apple-system, sans-serif",
                    fontSize: 21, fontWeight: 700, lineHeight: 1,
                    textAlign: "right",
                    color: top ? accent : "#eaeaef",
                    textShadow: top ? `0 0 16px ${accent}88` : "none",
                    letterSpacing: 1,
                  }}
                >
                  {row.points}
                </span>
                <div
                  style={{
                    height: 10,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.05)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${accent}55 0%, ${accent} 100%)`,
                      boxShadow: top ? `0 0 14px ${accent}AA` : `0 0 8px ${accent}44`,
                    }}
                  />
                </div>
              </div>
            );
          })}

          <div style={{ padding: "10px 10px 0", fontSize: 11, color: "#7a7d86", letterSpacing: 0.2 }}>
            Base points come from each player's popularity tier at battle time.
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 480px) {
          .pb-pc-formulas {
            grid-template-columns: 1fr !important;
          }
          .pb-pc-row {
            grid-template-columns: minmax(84px, 110px) 40px 1fr !important;
            gap: 8px !important;
          }
        }
      `}</style>
    </section>
  );
}
